// Where every bag stands on the photographic wall.
//
// The wall is a strip of bay photos ("plates") that share one camera, so every
// plate has the same three product boards at the same heights. This module
// turns a list of coffees into positions on those plates, following how a
// good shop merchandises a pipe-and-oak wall:
//
//   - true physical size (a 250 g pouch is ~13×20 cm, a 1 kg bag ~20×32 cm)
//   - bags stand ON the board, set back from the lip, never floating
//   - 3–4 coffees per board, air at the ends, props left where the plate has them
//   - stock depth: popular coffees get a second (or third) bag behind
//   - a small seeded wobble per coffee, so nothing sits on a perfect grid
//
// Everything is in plate fractions (0–1 of the plate's width / height) so the
// renderer can draw at any size.

// Measured from the generated plates (all four share this geometry).
// back = where the board's top surface meets the wall, front = its lip.
export const BOARDS = [
  { back: 0.334, front: 0.340, light: 1.0 },   // at eye level: surface barely visible
  { back: 0.578, front: 0.600, light: 0.97 },
  { back: 0.799, front: 0.842, light: 0.93 },
]
export const SPAN = { left: 0.095, right: 0.895 }     // inside the uprights
export const BAY_CM = 70                                // real width between uprights (sets the scale: boards ~38 cm apart)

// Props already in the photos, which bags must not stand in front of.
// plate index → board index → [from, to] x ranges.
export const OBSTACLES = {
  2: { 0: [[0.66, 0.90]] },          // stacked cups
  3: { 0: [[0.14, 0.36]] },          // the coffee tin
}

// Real sizes in cm by pack weight: [width, height] for a stand-up pouch.
function pouchCm (grams = 250, aspect = 0.72) {
  const h = grams >= 1000 ? 32 : grams >= 500 ? 26 : grams >= 300 ? 22 : 20
  if (aspect > 1.05) return [16 * aspect, 16]          // bundle boxes and flat packs
  if (aspect > 0.85) return [15.5 * aspect, 15.5]      // square box-bags
  return [h * aspect, h]
}

// Stable pseudo-random numbers per coffee, so a bag keeps its wobble.
function seeded (str) {
  let h = 2166136261
  for (const ch of str) { h ^= ch.charCodeAt(0); h = Math.imul(h, 16777619) }
  return () => { h ^= h << 13; h ^= h >>> 17; h ^= h << 5; return ((h >>> 0) % 10000) / 10000 }
}


/**
 * @param coffees  [{ slug, grams, aspect, inStock, facings? }]
 * @param opts     { plateCount?, startPlate?, plateAspect (w/h) }
 * @returns { plates: [plateIndex…], items: [{ slug, plate, board, x, base, w, h, ry, rz, facings, light, lean }] }
 */
export function layoutBay (coffees, { startPlate = 0, plateAspect = 768 / 1376 } = {}) {
  const cm = (SPAN.right - SPAN.left) / BAY_CM          // plate-width fraction per cm
  // How many bays this group needs: about 3 coffees a board, never more than 4.
  const nBays = Math.max(1, Math.ceil(coffees.length / 10))
  const slots = []                                      // [{ plate, board }] top to bottom, bay by bay
  for (let k = 0; k < nBays; k++) for (let b = 0; b < BOARDS.length; b++) slots.push({ plate: k, board: b, list: [] })
  // Planogram: the 1 kg blends take the bottom boards, everything else fills from eye level down.
  const big = coffees.filter((c) => c.kg), rest = coffees.filter((c) => !c.kg)
  const bottoms = slots.filter((s) => s.board === BOARDS.length - 1)
  big.forEach((c, n) => bottoms[n % bottoms.length].list.push({ ...c, grams: 1000 }))
  // A small feature bay (Special Release, a roaster with two coffees): each
  // coffee gets a board of its own as a hero — one bag, a little larger, with
  // air around it — from eye level down. No repeats, no rows of the same bag.
  if (!big.length && rest.length <= BOARDS.length && nBays === 1) {
    rest.forEach((c, b) => { slots[b].list.push({ ...c, hero: true }) })
  } else
  // Spread the rest so every board ends up with a similar count (fewest-first).
  for (const c of rest) {
    const room = slots.filter((s) => s.list.length < (OBSTACLES[(startPlate + s.plate) % 4]?.[s.board] ? 2 : 4) && !(s.board === BOARDS.length - 1 && s.list.some((x) => x.kg) && s.list.length >= 2))
    const target = room.sort((a, b) => a.list.length - b.list.length || a.plate - b.plate || a.board - b.board)[0] || slots[0]
    target.list.push(c)
  }
  const plates = Array.from({ length: nBays }, (_, k) => (startPlate + k) % 4)
  const items = []
  for (const slot of slots) {
    if (!slot.list.length) continue
    const b = slot.board
    const blocked = OBSTACLES[plates[slot.plate]]?.[b] || []
    const sized = slot.list.map((c) => {
      const [wcm0, hcm0] = pouchCm(c.grams, c.aspect)
      const [wcm, hcm] = c.hero ? [wcm0 * 1.2, hcm0 * 1.2] : [wcm0, hcm0]
      let h = hcm * cm * plateAspect                  // h as a fraction of plate HEIGHT (W/H = plateAspect)
      let w = wcm * cm
      // A bag has to clear the board above it, the way shelf spacing is chosen in a real shop.
      const above = b > 0 ? BOARDS[b - 1].front + 0.03 : 0.12    // below the board above AND the pipe under it
      const maxH = (BOARDS[b].back - above) * 0.9
      if (h > maxH) { w *= maxH / h; h = maxH }
      return { c, w, h }
    })
    // One facing per coffee: popularity shows as stock depth behind it, not as a
    // twin beside it (a pair of identical bags, each with its own ticket, reads as
    // a mistake). Every coffee gets the same air either side.
    const segments = carve(SPAN.left, SPAN.right, blocked)
    place(sized, segments).forEach(({ c, w, h, x, twin }, k) => {
      const rnd = seeded(c.slug + (twin ? `:twin${twin}` : '') + (c.repeat ? `:r${c.repeat}` : ''))
      const board = BOARDS[b]
      items.push({
        slug: c.slug,
        twin: !!twin,
        twinIndex: twin ? Number(twin) : 0,
        repeat: c.repeat || 0,
        plate: slot.plate,
        board: b,
        x: x + (rnd() - 0.5) * 0.008,                  // ±4 mm
        base: board.back + (board.front - board.back) * 0.55,
        w, h,
        ry: (rnd() - 0.5) * 7,                         // ±3.5° turned
        rz: (rnd() - 0.5) * 1.2,                       // barely off plumb
        lean: rnd() < 0.08 && k > 0 ? -3 : 0,          // the odd bag resting on its neighbour
        // Stock depth: 2 or 3 bags deep for anything in stock, one lonely bag when it's sold out.
        facings: c.facings ?? (c.inStock ? (rnd() < 0.5 ? 2 : 3) : 1),
        depthSide: rnd() < 0.5 ? -1 : 1,              // which way the stack behind drifts
        light: board.light,
        grams: c.grams,
      })
    })
  }
  return { plates, items }
}

/**
 * The whole wall: every bay of the current grouping in sequence on one
 * continuous strip of plates. The plate index carries on across bays and
 * cycles the four plates, so neighbouring plates always differ and the props
 * (cups, the tin) turn up at a natural rhythm rather than once per bay.
 *
 * @param bays  [{ key, coffees: [{ slug, grams, aspect, inStock, kg?, popular?, facings? }] }]
 * @param opts  { plateAspect?, plateCount? (distinct plate photos, default 4) }
 * @returns {
 *   plates: [photoIndex…]                         one entry per plate on the strip
 *   items:  [{ id, bay, slug, twin, plate, board, x, base, w, h, ry, rz, lean, facings, depthSide, light, grams }]
 *           plate is the strip index; x/w are plate-width fractions, base/h plate-height fractions
 *   bays:   [{ key, from, to }]                    plate range [from, to) of each bay
 * }
 */
export function layoutWall (bays, { plateAspect = 768 / 1376, plateCount = 4 } = {}) {
  const plates = []
  const items = []
  const ranges = []
  for (const bay of bays) {
    const from = plates.length
    if (!bay.coffees.length) continue
    const one = layoutBay(bay.coffees, { startPlate: from, plateAspect })
    one.plates.forEach((_, k) => plates.push((from + k) % plateCount))
    for (const it of one.items) {
      items.push({ ...it, id: it.slug + (it.twin ? `:twin${it.twinIndex > 1 ? it.twinIndex : ''}` : '') + (it.repeat ? `:r${it.repeat}` : ''), bay: bay.key, plate: from + it.plate })
    }
    ranges.push({ key: bay.key, from, to: plates.length })
  }
  return { plates, items, bays: ranges }
}

const FILL = 0.74

// Free x-ranges on a board once props are taken out.
function carve (a, z, blocked) {
  let segs = [[a, z]]
  for (const [ba, bz] of blocked) {
    segs = segs.flatMap(([s, e]) => (bz <= s || ba >= e) ? [[s, e]] : [[s, ba], [bz, e]].filter(([x, y]) => y - x > 0.04))
  }
  return segs
}

// Spread bags across the free segments with equal air between them and a
// little extra at the ends: shops leave the ends of a board open.
function place (sized, segments) {
  // A gap beside a prop that no bag fits in is not shelf space.
  const narrowest = Math.min(...sized.map((g) => g.w))
  const fits = segments.filter(([s, e]) => e - s >= narrowest * 1.15)
  if (fits.length) segments = fits
  const total = segments.reduce((n, [s, e]) => n + (e - s), 0)
  // A full board still leaves a hand's width between bags (and room for each
  // shelf ticket): if the bags would take more than FILL of the board, the
  // whole row is racked a touch smaller rather than jammed together.
  const want = sized.reduce((n, g) => n + g.w, 0)
  if (want > total * FILL) {
    const k = total * FILL / want
    sized = sized.map((g) => ({ ...g, w: g.w * k, h: g.h * k }))
  }
  const out = []
  let idx = 0
  for (const [s, e] of segments) {
    const share = Math.round(sized.length * (e - s) / total) || (idx < sized.length ? 1 : 0)
    const group = sized.slice(idx, idx + share)
    idx += group.length
    const used = group.reduce((n, g) => n + g.w, 0)
    const air = Math.max(0, (e - s) - used) / (group.length + 1.6)
    let x = s + air * 1.3
    const start = out.length
    group.forEach((g, n) => {
      const next = group[n + 1]
      out.push({ ...g, x: x + g.w / 2 })
      // A twin stands almost touching its sibling; the air it saves goes to the others.
      x += g.w + (next?.twin ? air * 0.12 : air * (1 + (group.some((q) => q.twin) ? 0.88 / Math.max(1, group.length - 2) : 0)))
    })
    // Whatever the gaps came to, the group sits centred on its stretch of board.
    const placed = out.slice(start)
    if (placed.length) {
      const left = placed[0].x - placed[0].w / 2
      const right = placed.at(-1).x + placed.at(-1).w / 2
      const shift = (s + e) / 2 - (left + right) / 2
      placed.forEach((g) => { g.x += shift })
    }
  }
  // Anything left over (rounding) goes at the end of the last segment.
  for (; idx < sized.length; idx++) out.push({ ...sized[idx], x: segments.at(-1)[1] - sized[idx].w })
  return out
}
